const cleanText = (value, maxLength = 240) =>
  String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);

const RIDER_NOTIFIED_OFFERS_KEY = "mozzoapp:we-rider:notified-offers:v1";
const CUSTOMER_TERMINAL_STATES = new Set([
  "delivered",
  "cancelled",
  "rejected",
  "refunded",
]);

const readNotifiedRiderOffers = () => {
  if (typeof window === "undefined") return new Set();
  try {
    const value = JSON.parse(
      window.localStorage.getItem(RIDER_NOTIFIED_OFFERS_KEY) || "[]"
    );
    return new Set(Array.isArray(value) ? value.map(String) : []);
  } catch {
    return new Set();
  }
};

const saveVisibleRiderOffers = (jobs) => {
  if (typeof window === "undefined") return;
  const visibleIds = (jobs || []).map((job) => String(job.id)).slice(0, 80);
  try {
    window.localStorage.setItem(
      RIDER_NOTIFIED_OFFERS_KEY,
      JSON.stringify(visibleIds)
    );
  } catch {
    // Las restricciones de almacenamiento no deben interrumpir Rider.
  }
  try {
    window.MozzoNotifications?.markRiderOffersSeen?.(
      JSON.stringify(visibleIds)
    );
  } catch {
    // El launcher anterior puede no incluir aun este puente nativo.
  }
};

const resolveAppUrl = (path) => {
  if (typeof window === "undefined") return "";
  try {
    return new URL(path || "/we-pedidos", window.location.origin).toString();
  } catch {
    return `${window.location.origin}/we-pedidos`;
  }
};

export const configureNativeRiderBackground = (configuration) => {
  if (
    typeof window === "undefined" ||
    typeof window.MozzoNotifications?.configureRiderBackground !== "function" ||
    !configuration?.deviceToken
  ) {
    return false;
  }
  try {
    return (
      window.MozzoNotifications.configureRiderBackground(
        JSON.stringify(configuration)
      ) !== false
    );
  } catch {
    return false;
  }
};

export const stopNativeRiderBackground = () => {
  if (
    typeof window === "undefined" ||
    typeof window.MozzoNotifications?.stopRiderBackground !== "function"
  ) {
    return false;
  }
  try {
    window.MozzoNotifications.stopRiderBackground();
    return true;
  } catch {
    return false;
  }
};

const resolveCustomerOrderState = (order) =>
  cleanText(order?.trackingStatus || order?.status, 60);

export const hasActiveWeCustomerOrders = (orders) =>
  (Array.isArray(orders) ? orders : []).some((order) => {
    const state = resolveCustomerOrderState(order);
    return Boolean(state) && !CUSTOMER_TERMINAL_STATES.has(state);
  });

export const configureNativeCustomerBackground = (configuration, orders = []) => {
  if (
    typeof window === "undefined" ||
    typeof window.MozzoNotifications?.configureCustomerBackground !== "function" ||
    !configuration?.deviceToken
  ) {
    return false;
  }
  const knownOrders = (Array.isArray(orders) ? orders : [])
    .map((order) => ({
      id: cleanText(order?.id, 120),
      state: resolveCustomerOrderState(order),
    }))
    .filter((order) => order.id && order.state)
    .slice(0, 50);
  try {
    return (
      window.MozzoNotifications.configureCustomerBackground(
        JSON.stringify({ ...configuration, knownOrders })
      ) !== false
    );
  } catch {
    return false;
  }
};

export const stopNativeCustomerBackground = () => {
  if (
    typeof window === "undefined" ||
    typeof window.MozzoNotifications?.stopCustomerBackground !== "function"
  ) {
    return false;
  }
  try {
    window.MozzoNotifications.stopCustomerBackground();
    return true;
  } catch {
    return false;
  }
};

export const showWeAppNotification = ({
  title,
  body,
  tag,
  path = "/we-pedidos",
}) => {
  if (typeof window === "undefined") return false;

  const payload = {
    title: cleanText(title, 100) || "We! pedidos",
    body: cleanText(body, 300),
    tag: cleanText(tag, 120) || `we-pedidos-${Date.now()}`,
    url: resolveAppUrl(path),
  };

  try {
    if (typeof window.MozzoNotifications?.notify === "function") {
      const notified = window.MozzoNotifications.notify(JSON.stringify(payload));
      if (notified !== false) return true;
    }
  } catch {
    // Sigue con la notificacion del navegador si el puente nativo no responde.
  }

  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return false;
  }

  try {
    const notification = new Notification(payload.title, {
      body: payload.body,
      tag: payload.tag,
      renotify: true,
      silent: false,
    });
    notification.onclick = () => {
      window.focus();
      window.location.assign(payload.url);
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
};

const RIDER_STATUS_NOTIFICATIONS = Object.freeze({
  ready_for_pickup: {
    title: "Pedido listo para retirar",
    body: "El restaurante termino el pedido. Acercate al mostrador para retirarlo.",
  },
  arrived_pickup: {
    title: "Retiro pendiente de codigo",
    body: "Pide al restaurante el codigo de seis numeros para retirar el pedido.",
  },
  picked_up: {
    title: "Retiro verificado",
    body: "Ya puedes iniciar el viaje hacia el domicilio del cliente.",
  },
  in_transit: {
    title: "Entrega iniciada",
    body: "Al llegar, solicita al cliente su codigo de entrega.",
  },
  cancelled: {
    title: "Entrega cancelada",
    body: "La entrega activa fue cancelada. Revisa tu panel de Rider.",
  },
});

export const notifyRiderDashboardChanges = (previous, next) => {
  if (!next) return;

  const previousAvailableIds = new Set(
    (previous?.availableJobs || []).map((job) => job.id)
  );
  const alreadyNotifiedIds = readNotifiedRiderOffers();
  const newJobs = (next.availableJobs || []).filter(
    (job) =>
      !previousAvailableIds.has(job.id) &&
      !alreadyNotifiedIds.has(String(job.id))
  );
  saveVisibleRiderOffers(next.availableJobs || []);
  if (newJobs.length) {
    const firstJob = newJobs[0];
    showWeAppNotification({
      title:
        newJobs.length > 1
          ? `${newJobs.length} nuevas ofertas de envio`
          : "Nueva oferta de envio",
      body: `${firstJob.pickup?.name || "Restaurante"} · ${
        firstJob.displayNumber || "Pedido disponible"
      }`,
      tag: `we-rider-offer-${firstJob.id}`,
      path: "/we-pedidos/rider",
    });
  }

  const previousJob = previous?.activeJob;
  const nextJob = next.activeJob;
  if (previousJob?.id && nextJob?.id === previousJob.id) {
    const statusChanged = nextJob.status !== previousJob.status;
    const pickupBecameReady =
      nextJob.status === "arrived_pickup" &&
      !["ready_for_delivery", "assigned_to_driver", "out_for_delivery"].includes(
        previousJob.orderStatus
      ) &&
      ["ready_for_delivery", "assigned_to_driver", "out_for_delivery"].includes(
        nextJob.orderStatus
      );
    const notification = statusChanged
      ? RIDER_STATUS_NOTIFICATIONS[nextJob.status]
      : pickupBecameReady
        ? RIDER_STATUS_NOTIFICATIONS.ready_for_pickup
        : null;
    if (notification) {
      showWeAppNotification({
        ...notification,
        tag: `we-rider-${nextJob.id}-${nextJob.status}`,
        path: "/we-pedidos/rider",
      });
    }
  }
};

export const notifyCustomerOrderChanges = (previousOrders, nextOrders) => {
  if (!Array.isArray(previousOrders) || !Array.isArray(nextOrders)) return;

  const previousById = new Map(previousOrders.map((order) => [order.id, order]));
  nextOrders.forEach((order) => {
    const previous = previousById.get(order.id);
    if (!previous) return;
    const previousState = previous.trackingStatus || previous.status;
    const nextState = order.trackingStatus || order.status;
    if (!nextState || nextState === previousState) return;

    showWeAppNotification({
      title: order.statusLabel || "Tu pedido se actualizo",
      body: `${order.restaurantName || "Restaurante"} · Pedido ${
        order.displayNumber || "actualizado"
      }`,
      tag: `we-customer-${order.id}-${nextState}`,
      path: order.trackingUrl || "/we-pedidos/pedidos",
    });
  });
};

export const notifyCustomerTrackingChange = (previous, next, trackingToken) => {
  if (!previous || !next || previous.status === next.status) return;
  showWeAppNotification({
    title: next.statusLabel || "Tu pedido se actualizo",
    body: next.customerMessage || next.restaurantName || "Revisa el seguimiento.",
    tag: `we-tracking-${trackingToken}-${next.status}`,
    path: `/we-pedidos/seguimiento/${encodeURIComponent(trackingToken)}`,
  });
};
